import type React from 'react'

/**
 * Canonical CSS-variable values from `docs/pantry/2026.04.01` (`breadstack-categories.html`,
 * `breadstack-orders.html` `:root`). Mock HTML still names brand hues `--green*`; values are blue.
 *
 * Categories adds `--amber-mid` and `--blue-mid`; segment/audience mocks add `--indigo` / `--indigo-bg`.
 */
export const pantryPalette = {
  green: '#2563EB',
  greenH: '#1D4ED8',
  greenLight: '#EFF6FF',
  greenMid: '#BFDBFE',
  amber: '#B45309',
  amberBg: '#FEF3C7',
  amberMid: '#FDE68A',
  blue: '#1D4ED8',
  blueBg: '#EFF6FF',
  blueMid: '#BFDBFE',
  red: '#DC2626',
  redBg: '#FEF2F2',
  purple: '#7C3AED',
  purpleBg: '#F5F3FF',
  teal: '#0F766E',
  tealBg: '#F0FDFA',
  orange: '#EA580C',
  orangeBg: '#FFF7ED',
  indigo: '#4338CA',
  indigoBg: '#EEF2FF',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
} as const

/** App-level color tokens (structured for components). Values = pantryPalette. */
export const pantryColors = {
  white: '#FFFFFF',
  black: '#111827',
  brand: {
    primary: pantryPalette.green,
    hover: pantryPalette.greenH,
    light: pantryPalette.greenLight,
    mid: pantryPalette.greenMid,
  },
  gray: {
    50: pantryPalette.gray50,
    100: pantryPalette.gray100,
    200: pantryPalette.gray200,
    300: pantryPalette.gray300,
    400: pantryPalette.gray400,
    500: pantryPalette.gray500,
    600: pantryPalette.gray600,
    700: pantryPalette.gray700,
    800: pantryPalette.gray800,
    900: '#111827',
  },
  blue: {
    50: pantryPalette.blueBg,
    200: pantryPalette.blueMid,
    700: pantryPalette.blue,
  },
  teal: {
    50: pantryPalette.tealBg,
    200: '#99F6E4',
    700: pantryPalette.teal,
  },
  purple: {
    50: pantryPalette.purpleBg,
    200: '#E9D5FF',
    700: pantryPalette.purple,
  },
  amber: {
    50: '#FFFBEB',
    100: pantryPalette.amberBg,
    200: pantryPalette.amberMid,
    700: pantryPalette.amber,
    800: '#92400E',
  },
  orange: {
    50: pantryPalette.orangeBg,
    700: pantryPalette.orange,
  },
  red: {
    50: pantryPalette.redBg,
    100: '#FEE2E2',
    200: '#FECACA',
    700: pantryPalette.red,
    800: '#991B1B',
  },
  indigo: {
    50: pantryPalette.indigoBg,
    700: pantryPalette.indigo,
  },
  /** VIP / highlight chips (from mock `.seg-badge-xs.vip`) */
  yellow: {
    50: '#FFF7ED',
    300: '#FDBA74',
    800: '#C2410C',
  },
} as const

/** Matches `--r-sm` / `--r-md` / `--r-lg` in 2026.04.01 mocks */
export const pantryRadii = {
  sm: 6,
  md: 10,
  lg: 16,
  xl: 16,
  pill: 999,
} as const

export const pantrySpacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  '2xl': 24,
  '3xl': 28,
} as const

/** Matches `--shadow-sm` / `--shadow-md` / `--shadow-lg` in 2026.04.01 mocks */
export const pantryShadows = {
  sm: '0 1px 3px rgba(0, 0, 0, 0.08)',
  md: '0 4px 12px rgba(0, 0, 0, 0.1)',
  lg: '0 8px 28px rgba(0, 0, 0, 0.16)',
} as const

export const pantryTypography = {
  fontFamily:
    'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  pageTitle: { fontSize: 20, fontWeight: 700, lineHeight: 1.2 },
  sectionTitle: { fontSize: 17, fontWeight: 700, lineHeight: 1.3 },
  label: { fontSize: 12, fontWeight: 500, lineHeight: 1.4 },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '0.06em',
  },
  body: { fontSize: 13.5, fontWeight: 400, lineHeight: 1.5 },
  bodySm: { fontSize: 13, fontWeight: 400, lineHeight: 1.5 },
  caption: { fontSize: 11.5, fontWeight: 400, lineHeight: 1.4 },
  captionXs: { fontSize: 11, fontWeight: 400, lineHeight: 1.4 },
} as const

/** `--nav-w` / `--drawer-w` from `docs/pantry/2026.04.01/breadstack-orders.html` */
export const pantryLayout = {
  navWidth: 220,
  drawerWidth: 520,
} as const

export const pantryFocusRing = '0 0 0 2px rgba(37, 99, 235, 0.18)'

export const pantryBaseText: React.CSSProperties = {
  fontFamily: pantryTypography.fontFamily,
  color: pantryColors.gray[800],
}

export const pantryTokens = {
  colors: pantryColors,
  radii: pantryRadii,
  spacing: pantrySpacing,
  shadows: pantryShadows,
  typography: pantryTypography,
  layout: pantryLayout,
  focusRing: pantryFocusRing,
} as const
